import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Users, Plus, Bot } from "lucide-react";
import { useGameStore } from "@/lib/gameStore";
import { generateRoomCode } from "@/lib/gameUtils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { validatePlayerName } from "@/lib/validationSchemas";
import { mapDatabaseError } from "@/lib/errorUtils";
import Tutorial from "./Tutorial";
import PlayerStats from "./PlayerStats";
import DailyChallengeCard from "./DailyChallenge";
import { useStreaks } from "@/hooks/useStreaks";
import { useXP } from "@/hooks/useXP";
import SEOFooter from "./SEOFooter";
import { loadPlayerCosmetics, savePlayerCosmetics, PlayerCosmetics } from "@/lib/cosmetics";
type MusicIntensity = 'calm' | 'waiting' | 'medium' | 'intense' | 'critical' | 'victory' | 'defeat';
interface MenuScreenProps {
  onNavigate: (screen: "lobby" | "join" | "practice") => void;
  setMusicIntensity: (intensity: MusicIntensity) => void;
  pendingRoomCode?: string;
  onClearPendingRoomCode?: () => void;
}
export default function MenuScreen({
  onNavigate,
  setMusicIntensity,
  pendingRoomCode,
  onClearPendingRoomCode
}: MenuScreenProps) {
  const [showTutorial, setShowTutorial] = useState(false);
  const {
    playerName,
    setPlayerName
  } = useGameStore();
  const [cosmetics, setCosmetics] = useState<PlayerCosmetics>(loadPlayerCosmetics);
  const {
    streakData,
    dailyChallenges
  } = useStreaks();
  const {
    xpData,
    getLevelProgress
  } = useXP();

  // Check if first visit (but not if there's a pending room code)
  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('67chaos-tutorial-seen');
    if (!hasSeenTutorial && !pendingRoomCode) {
      setShowTutorial(true);
    }
  }, [pendingRoomCode]);

  // Set calm music intensity when on menu
  useEffect(() => {
    setMusicIntensity('calm');
  }, [setMusicIntensity]);
  const {
    setPlayerId,
    setCurrentRoom
  } = useGameStore();

  // Auto-navigate to join when name is entered and there's a pending room code
  useEffect(() => {
    if (pendingRoomCode && playerName.trim().length >= 2) {
      // Small delay to let user see their name before navigating
      const timer = setTimeout(() => {
        onClearPendingRoomCode?.();
        onNavigate("join");
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [pendingRoomCode, playerName, onNavigate, onClearPendingRoomCode]);

  // Handle join button click - navigate to join with pending room code
  const handleJoinClick = () => {
    onClearPendingRoomCode?.();
    onNavigate("join");
  };
  const [isCreating, setIsCreating] = useState(false);
  const {
    toast
  } = useToast();
  const handleCosmeticsChange = (newCosmetics: PlayerCosmetics) => {
    setCosmetics(newCosmetics);
    savePlayerCosmetics(newCosmetics);
  };
  const handleCreateRoom = async () => {
    // Validate player name
    const nameError = validatePlayerName(playerName);
    if (nameError) {
      toast({
        title: "Invalid name",
        description: nameError,
        variant: "destructive"
      });
      return;
    }
    setIsCreating(true);
    try {
      // Sign in anonymously to get auth_user_id
      const {
        data: authData,
        error: authError
      } = await supabase.auth.signInAnonymously();
      if (authError) throw authError;
      const authUserId = authData.user.id;
      const roomCode = generateRoomCode();

      // Create room in database with auth_user_id
      const {
        error: roomError
      } = await supabase.from("rooms").insert({
        id: roomCode,
        host_id: "temp",
        status: "lobby",
        auth_user_id: authUserId
      });
      if (roomError) throw roomError;

      // Create player with auth_user_id
      const {
        data: player,
        error: playerError
      } = await supabase.from("players").insert({
        room_id: roomCode,
        name: playerName,
        is_host: true,
        auth_user_id: authUserId
      }).select().single();
      if (playerError) throw playerError;

      // Update room with actual host_id
      await supabase.from("rooms").update({
        host_id: player.id
      }).eq("id", roomCode);
      setPlayerId(player.id);
      setCurrentRoom({
        id: roomCode,
        hostId: player.id,
        status: "lobby",
        currentRound: 0,
        currentTotal: 0,
        targetTotal: 67,
        players: [{
          id: player.id,
          name: playerName,
          isHost: true,
          stressLevel: "calm",
          isActive: true
        }],
        hasSelected: false,
        roundTimeRemaining: 3
      });
      onNavigate("lobby");
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error("Error creating room (dev only):", error);
      }
      toast({
        title: "Failed to create room",
        description: mapDatabaseError(error),
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
    }
  };
  return <div className="min-h-screen min-h-[100dvh] flex flex-col relative overflow-hidden">
      {/* Tutorial */}
      <AnimatePresence>
        {showTutorial && <Tutorial onClose={() => setShowTutorial(false)} />}
      </AnimatePresence>

      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-card to-background" />
      <motion.div className="absolute inset-0 opacity-30" animate={{
      backgroundPosition: ["0% 0%", "100% 100%"]
    }} transition={{
      duration: 20,
      repeat: Infinity,
      repeatType: "reverse"
    }} style={{
      backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.03) 10px, rgba(255,255,255,0.03) 20px)"
    }} />

      {/* Main Content - Centered */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 relative z-10">
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="w-full max-w-md space-y-6">
          {/* Hero Logo */}
          <header className="text-center space-y-4">
            <motion.h1 className="text-7xl md:text-8xl font-black uppercase tracking-tighter text-shadow" animate={{
            textShadow: ["0 0 20px hsl(160 84% 39% / 0.5)", "0 0 40px hsl(160 84% 39% / 0.8)", "0 0 20px hsl(160 84% 39% / 0.5)"]
          }} transition={{
            duration: 2,
            repeat: Infinity
          }}>
              <span className="inline-flex items-baseline">
                <span className="text-primary translate-y-[-0.2em]">6</span>
                <span className="text-primary">7</span>
              </span>{" "}
              <span className="text-foreground">CHAOS</span>
            </motion.h1>
            <p className="text-muted-foreground text-lg font-semibold">Hit exactly 67. Together. Or lose trying.</p>
            <p className="text-sm text-muted-foreground">
              Based on the viral 67 meme — Dictionary.com's 2025 Word of the Year
            </p>
          </header>

          {/* Player Stats Card */}
          {xpData.totalXP > 0 && <PlayerStats level={xpData.level} totalXP={xpData.totalXP} levelProgress={getLevelProgress()} streak={streakData.currentStreak} totalGames={streakData.totalGamesPlayed} totalWins={streakData.totalWins} cosmetics={cosmetics} onCosmeticsChange={handleCosmeticsChange} />}

          {/* Name Input Card */}
          <Card className="glass-card p-6 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Your Name</label>
              <Input value={playerName} onChange={e => {
              const value = e.target.value.slice(0, 20);
              const filtered = value.replace(/[^a-zA-Z0-9 ]/g, "");
              setPlayerName(filtered);
            }} placeholder="Enter name (max 20 chars)" className="bg-secondary/50 border-2 border-border/50 h-14 text-lg font-semibold" maxLength={20} autoFocus={!!pendingRoomCode} />
            </div>

            {/* Pending room code indicator */}
            {pendingRoomCode && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex items-center justify-center gap-2 py-2 px-4 bg-primary/20 rounded-lg border border-primary/30">
                <Users className="h-4 w-4 text-primary" />
                <span className="text-sm font-bold text-primary">
                  Joining room: {pendingRoomCode}
                </span>
              </motion.div>}

            <div className="grid gap-4 pt-2">
              {/* Practice Mode - Solo option first */}
              {!pendingRoomCode && (
                <Button onClick={() => onNavigate("practice")} disabled={!playerName.trim()} variant="outline" className="h-14 text-lg font-bold uppercase tracking-wide hover:scale-105 transition-transform border-primary/30 hover:border-primary/60">
                  <Bot className="mr-2 h-5 w-5" />
                  Practice Mode (Solo)
                </Button>
              )}

              {/* Play with Friends section */}
              {!pendingRoomCode && (
                <div className="space-y-3">
                  <p className="text-sm font-bold uppercase tracking-wide text-muted-foreground text-center">Play with Friends</p>
                  <div className="grid gap-3">
                    <Button onClick={handleCreateRoom} disabled={isCreating || !playerName.trim()} className="h-16 text-lg font-bold uppercase tracking-wide glow-green hover:scale-105 transition-transform">
                      <Plus className="mr-2 h-6 w-6" />
                      Create Room
                    </Button>
                    <Button onClick={handleJoinClick} disabled={!playerName.trim()} variant="secondary" className="h-16 text-lg font-bold uppercase tracking-wide hover:scale-105 transition-transform">
                      <Users className="mr-2 h-6 w-6" />
                      Join Room
                    </Button>
                  </div>
                </div>
              )}

              {/* When there's a pending room code, show join button prominently */}
              {pendingRoomCode && (
                <Button onClick={handleJoinClick} disabled={!playerName.trim()} className="h-16 text-lg font-bold uppercase tracking-wide glow-green hover:scale-105 transition-transform">
                  <Users className="mr-2 h-6 w-6" />
                  Join Room {pendingRoomCode}
                </Button>
              )}
            </div>
          </Card>

          {/* Daily Challenges */}
          {dailyChallenges.length > 0 && <DailyChallengeCard challenges={dailyChallenges} streak={streakData.currentStreak} />}

          {/* How to Play & Links */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.3
        }} className="text-center space-y-4">
            <div className="gap-[5px] items-start justify-center flex flex-row">
              <Button variant="ghost" onClick={() => setShowTutorial(true)} className="font-bold text-muted-foreground hover:text-foreground">
                
                How to Play
              </Button>
              <Link to="/how-to-play">
                <Button variant="ghost" className="font-bold text-muted-foreground hover:text-foreground">
                  
                  Full Guide
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="ghost" className="font-bold text-muted-foreground hover:text-foreground">
                  
                  About
                </Button>
              </Link>
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <p className="font-semibold">🎯 Pick numbers each round</p>
              <p className="font-semibold">🔴🟡🟢 Colors guide you to 67</p>
              <p className="font-semibold">💀 Work together or chaos wins</p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* SEO Content Section - Below the fold */}
      <section className="relative z-10 bg-card/30 border-t border-border/30 px-4 py-12">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* What is 67 CHAOS */}
          <article className="space-y-4">
            <h2 className="text-2xl font-bold text-foreground">What is 67 CHAOS?</h2>
            <p className="text-muted-foreground">
              67 CHAOS is a free online multiplayer party game where players work together to 
              hit exactly 67 points. Inspired by the viral 67 meme that took Gen Alpha by storm, 
              this browser-based game brings the chaos and collaboration of the meme to life. 
              Each round, players secretly pick numbers, and all choices are added together. 
              The catch? You can't see the total — only color hints guide you!
            </p>
          </article>

          {/* Quick FAQ for AEO */}
          <article className="space-y-4">
            <h2 className="text-2xl font-bold text-foreground">Frequently Asked Questions</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">How do I play 67 CHAOS?</h3>
                <p className="text-sm text-muted-foreground">
                  Enter your name, create or join a room, share the code with friends, 
                  and pick numbers each round to reach exactly 67 together.
                </p>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">Is 67 CHAOS free?</h3>
                <p className="text-sm text-muted-foreground">
                  Yes! 67 CHAOS is completely free to play. No downloads or accounts required.
                </p>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">What is the 67 meme?</h3>
                <p className="text-sm text-muted-foreground">
                  The 67 meme is a viral Gen Alpha phenomenon recognized as Dictionary.com's 
                  2025 Word of the Year, symbolizing collective chaos and fun.
                </p>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">Can I play on mobile?</h3>
                <p className="text-sm text-muted-foreground">
                  Absolutely! 67 CHAOS is fully optimized for mobile with touch controls 
                  and haptic feedback. Works on iOS and Android browsers.
                </p>
              </div>
            </div>
          </article>

          {/* Features */}
          <article className="space-y-4">
            <h2 className="text-2xl font-bold text-foreground">Game Features</h2>
            <ul className="grid md:grid-cols-3 gap-3 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">✅ 2-8 Player Multiplayer</li>
              <li className="flex items-center gap-2">✅ Real-time Gameplay</li>
              <li className="flex items-center gap-2">✅ Solo Practice Mode</li>
              <li className="flex items-center gap-2">✅ Mobile & Desktop</li>
              <li className="flex items-center gap-2">✅ Dynamic Music & Sound</li>
              <li className="flex items-center gap-2">✅ XP & Progression System</li>
              <li className="flex items-center gap-2">✅ Daily Challenges</li>
              <li className="flex items-center gap-2">✅ Shareable Result Cards</li>
              <li className="flex items-center gap-2">✅ No Download Required</li>
            </ul>
          </article>
        </div>
      </section>

      {/* SEO Footer */}
      <SEOFooter />
    </div>;
}